import React, {useEffect, useRef} from 'react';

/**
 * ParticleMorphStory
 * A scroll-driven particle animation: a swarm of particles pinned in the
 * viewport morphs between shapes while story panels scroll past.
 *
 * Phase keys (one per panel):
 *   0 logo (hero)  →  1 loose cloud (intro)  →  2 server (Systems)
 *   →  3 code (Development)  →  4 aperture (Studios)  →  5 logo (outro)
 */

type ShapePoint = { x: number; y: number; r: number; g: number; b: number };

const CREAM: [number, number, number] = [248, 247, 242];
const TEAL: [number, number, number] = [27, 162, 122];
const PURPLE: [number, number, number] = [92, 51, 246];
const CORAL: [number, number, number] = [255, 90, 82];

// Path data taken from /pics/logo/sec/dd_logo_only_colors_sec.svg (viewBox 619x936)
const LOGO_PATH =
    'M482.808 309.051L481.982 178.484C481.67 129.134 441.674 89.2403 392.323 89.0541L203.34 88.3409C153.502 88.1528 113 128.502 113 178.34V761.251C113 810.956 153.294 851.251 203 851.251H392.808C442.514 851.251 482.808 810.956 482.808 761.251V495.345';
const LOGO_RECTS: Array<{ x: number; y: number; w: number; c: [number, number, number] }> = [
    {x: 398.003, y: 345.198, w: 108.44, c: PURPLE},
    {x: 263.147, y: 282.636, w: 69.5128, c: TEAL},
    {x: 315.978, y: 482.833, w: 52.8298, c: CORAL}
];

const KEYS = 6; // number of phase keyframes

export interface StoryPanel {
    content: React.ReactNode;
    /** Snap the viewport to this panel while scrolling nearby. */
    snap?: boolean;
}

interface Props {
    panels: StoryPanel[];
}

const ParticleMorphStory: React.FC<Props> = ({panels}) => {
    const sectionRef = useRef<HTMLElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const section = sectionRef.current;
        const canvas = canvasRef.current;
        if (!section || !canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let W = 0;
        let H = 0;
        let dpr = 1;
        let NP = 0;
        let isMobile = false;
        let shapes: ShapePoint[][] = []; // KEYS entries, each of length NP (canvas px coords)
        let rafId = 0;
        let running = false;
        let destroyed = false;

        const mouse = {x: -9999, y: -9999};

        type Particle = {
            x: number; y: number;
            r: number; g: number; b: number;
            size: number; wob: number; spd: number; ph: number; sw: number;
        };
        let parts: Particle[] = [];

        const rand = (a: number, b: number) => a + Math.random() * (b - a);
        const smooth = (t: number) => t * t * (3 - 2 * t);

        const shuffle = <T, >(arr: T[]): T[] => {
            for (let i = arr.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }
            return arr;
        };

        /** Sample white/colored pixels from an offscreen drawing into points. */
        const samplePoints = (
            draw: (c: CanvasRenderingContext2D, S: number) => void,
            color: [number, number, number] | null
        ): Array<{ x: number; y: number; r: number; g: number; b: number }> => {
            const S = 480;
            const off = document.createElement('canvas');
            off.width = S;
            off.height = S;
            const oc = off.getContext('2d', {willReadFrequently: true});
            if (!oc) return [];
            oc.clearRect(0, 0, S, S);
            draw(oc, S);
            const data = oc.getImageData(0, 0, S, S).data;
            const pts: Array<{ x: number; y: number; r: number; g: number; b: number }> = [];
            const step = 4;
            for (let y = 0; y < S; y += step) {
                for (let x = 0; x < S; x += step) {
                    const i = (y * S + x) * 4;
                    if (data[i + 3] > 120) {
                        pts.push({
                            x: (x - S / 2) / S,
                            y: (y - S / 2) / S,
                            r: color ? color[0] : data[i],
                            g: color ? color[1] : data[i + 1],
                            b: color ? color[2] : data[i + 2]
                        });
                    }
                }
            }
            return shuffle(pts);
        };

        const roundedRect = (c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
            c.beginPath();
            c.moveTo(x + r, y);
            c.arcTo(x + w, y, x + w, y + h, r);
            c.arcTo(x + w, y + h, x, y + h, r);
            c.arcTo(x, y + h, x, y, r);
            c.arcTo(x, y, x + w, y, r);
            c.closePath();
        };

        const drawLogo = (c: CanvasRenderingContext2D, S: number) => {
            // Fit the 619x936 logo into the box
            const scale = (S * 0.92) / 936;
            c.save();
            c.translate(S / 2 - (619 * scale) / 2, S / 2 - (936 * scale) / 2);
            c.scale(scale, scale);
            c.strokeStyle = 'rgb(248,247,242)';
            c.lineWidth = 54;
            c.stroke(new Path2D(LOGO_PATH));
            LOGO_RECTS.forEach(rc => {
                c.fillStyle = `rgb(${rc.c[0]},${rc.c[1]},${rc.c[2]})`;
                c.fillRect(rc.x, rc.y, rc.w, rc.w);
            });
            c.restore();
        };

        const drawServer = (c: CanvasRenderingContext2D, S: number) => {
            c.strokeStyle = c.fillStyle = '#fff';
            c.lineWidth = 13;
            const x = S * 0.18, w = S * 0.64, rowH = S * 0.19;
            for (let i = 0; i < 3; i++) {
                const y = S * 0.22 + i * (rowH + S * 0.035);
                roundedRect(c, x, y, w, rowH, 14);
                c.stroke();
                // LED
                c.beginPath();
                c.arc(x + w - S * 0.07, y + rowH / 2, S * 0.02, 0, Math.PI * 2);
                c.fill();
                // vents
                for (let v = 0; v < 3; v++) {
                    c.beginPath();
                    c.moveTo(x + S * 0.06, y + rowH * (0.3 + v * 0.2));
                    c.lineTo(x + S * 0.3, y + rowH * (0.3 + v * 0.2));
                    c.lineWidth = 7;
                    c.stroke();
                    c.lineWidth = 13;
                }
            }
        };

        const drawCode = (c: CanvasRenderingContext2D, S: number) => {
            c.fillStyle = '#fff';
            c.font = `900 ${S * 0.42}px Montserrat, 'Segoe UI', monospace`;
            c.textAlign = 'center';
            c.textBaseline = 'middle';
            c.fillText('</>', S / 2, S * 0.54);
        };

        const drawAperture = (c: CanvasRenderingContext2D, S: number) => {
            const cx = S / 2, cy = S / 2;
            const R = S * 0.36, hexR = S * 0.15;
            c.strokeStyle = '#fff';
            c.lineWidth = 13;
            c.beginPath();
            c.arc(cx, cy, R, 0, Math.PI * 2);
            c.stroke();
            const vx = (a: number, r: number) => cx + r * Math.cos(a);
            const vy = (a: number, r: number) => cy + r * Math.sin(a);
            c.lineWidth = 10;
            for (let i = 0; i < 6; i++) {
                const a = (i / 6) * Math.PI * 2;
                const b = ((i + 1) / 6) * Math.PI * 2;
                // inner hexagon edge
                c.beginPath();
                c.moveTo(vx(a, hexR), vy(a, hexR));
                c.lineTo(vx(b, hexR), vy(b, hexR));
                c.stroke();
                // blade from hexagon vertex to outer ring
                c.beginPath();
                c.moveTo(vx(a, hexR), vy(a, hexR));
                c.lineTo(vx(a + 0.62, R), vy(a + 0.62, R));
                c.stroke();
            }
        };

        /** Build all keyframe target sets in canvas pixel coordinates. */
        const buildShapes = () => {
            isMobile = W < 900;
            const scale = Math.min(W, H) * (isMobile ? 0.82 : 0.6);

            const logoRaw = samplePoints(drawLogo, null);
            const serverRaw = samplePoints(drawServer, TEAL);
            const codeRaw = samplePoints(drawCode, PURPLE);
            const apertureRaw = samplePoints(drawAperture, CORAL);

            NP = Math.max(600, Math.min(2200, Math.floor((W * H) / 900)));
            if (isMobile) NP = Math.min(NP, 900);

            // (Re)seed particles if count changed
            if (parts.length !== NP) {
                parts = [];
                for (let i = 0; i < NP; i++) {
                    parts.push({
                        x: Math.random() * W,
                        y: Math.random() * H,
                        r: CREAM[0], g: CREAM[1], b: CREAM[2],
                        size: rand(1.4, 2.8),
                        wob: rand(1, 3.2),
                        spd: rand(0.4, 1.3),
                        ph: rand(0, Math.PI * 2),
                        sw: rand(-1, 1)
                    });
                }
            }

            const centers: Array<[number, number]> = [
                // Hero logo: centered between the floating header (~8vh) and the
                // headline block (~67vh on desktop, ~60vh on mobile)
                [W * 0.5, H * (isMobile ? 0.34 : 0.38)],
                [W * 0.5, H * 0.5], // cloud
                isMobile ? [W * 0.5, H * 0.34] : [W * 0.33, H * 0.5], // server
                isMobile ? [W * 0.5, H * 0.34] : [W * 0.33, H * 0.5], // code
                isMobile ? [W * 0.5, H * 0.34] : [W * 0.33, H * 0.5], // aperture
                [W * 0.5, H * 0.46] // outro logo
            ];

            const toSet = (
                raw: Array<{ x: number; y: number; r: number; g: number; b: number }>,
                center: [number, number],
                shapeScale: number
            ): ShapePoint[] => {
                const out: ShapePoint[] = [];
                const n = raw.length || 1;
                for (let i = 0; i < NP; i++) {
                    const p = raw[i % n] || {x: 0, y: 0, r: 248, g: 247, b: 242};
                    out.push({
                        x: center[0] + p.x * shapeScale,
                        y: center[1] + p.y * shapeScale,
                        r: p.r, g: p.g, b: p.b
                    });
                }
                return out;
            };

            // Loose cloud: ring of drifting particles
            const cloud: ShapePoint[] = [];
            for (let i = 0; i < NP; i++) {
                const a = Math.random() * Math.PI * 2;
                const rr = scale * rand(0.22, 0.52);
                cloud.push({
                    x: centers[1][0] + rr * Math.cos(a),
                    y: centers[1][1] + rr * Math.sin(a) * 0.75,
                    r: CREAM[0], g: CREAM[1], b: CREAM[2]
                });
            }

            shapes = [
                toSet(logoRaw, centers[0], scale * (isMobile ? 0.75 : 0.85)),
                cloud,
                toSet(serverRaw, centers[2], scale),
                toSet(codeRaw, centers[3], scale),
                toSet(apertureRaw, centers[4], scale),
                toSet(logoRaw, centers[5], scale * (isMobile ? 0.9 : 1.0))
            ];
        };

        const resize = () => {
            dpr = Math.min(2, window.devicePixelRatio || 1);
            W = canvas.clientWidth;
            H = canvas.clientHeight;
            canvas.width = Math.floor(W * dpr);
            canvas.height = Math.floor(H * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            buildShapes();
        };

        const getProgress = (): number => {
            const rect = section.getBoundingClientRect();
            const vh = window.innerHeight || 1;
            const total = rect.height - vh;
            if (total <= 0) return 0;
            return Math.min(1, Math.max(0, -rect.top / total));
        };

        const drawFrame = (time: number) => {
            const t = getProgress() * (KEYS - 1);
            const i = Math.min(KEYS - 2, Math.floor(t));
            const f = smooth(Math.min(1, Math.max(0, t - i)));
            const swirlAmp = Math.sin(Math.PI * f) * Math.min(W, H) * 0.12;
            const A = shapes[i];
            const B = shapes[i + 1];
            if (!A || !B) return;

            ctx.clearRect(0, 0, W, H);
            ctx.globalCompositeOperation = 'lighter';

            const ease = reduced ? 1 : 0.085;
            const tm = time * 0.001;

            for (let k = 0; k < NP; k++) {
                const p = parts[k];
                const a = A[k];
                const b = B[k];
                const dx = b.x - a.x;
                const dy = b.y - a.y;
                let tx = a.x + dx * f;
                let ty = a.y + dy * f;

                if (!reduced) {
                    // Swirl perpendicular to travel direction during transitions
                    const len = Math.hypot(dx, dy) || 1;
                    tx += (-dy / len) * swirlAmp * p.sw;
                    ty += (dx / len) * swirlAmp * p.sw;
                    // Idle breathing
                    tx += Math.cos(tm * p.spd + p.ph) * p.wob;
                    ty += Math.sin(tm * p.spd * 1.3 + p.ph) * p.wob;
                    // Mouse repulsion
                    const mdx = p.x - mouse.x;
                    const mdy = p.y - mouse.y;
                    const md = Math.hypot(mdx, mdy);
                    if (md < 90 && md > 0.01) {
                        const push = ((90 - md) / 90) * 14;
                        tx += (mdx / md) * push;
                        ty += (mdy / md) * push;
                    }
                }

                p.x += (tx - p.x) * ease;
                p.y += (ty - p.y) * ease;
                p.r += (a.r + (b.r - a.r) * f - p.r) * ease;
                p.g += (a.g + (b.g - a.g) * f - p.g) * ease;
                p.b += (a.b + (b.b - a.b) * f - p.b) * ease;

                ctx.fillStyle = `rgba(${p.r | 0},${p.g | 0},${p.b | 0},0.88)`;
                const s = p.size;
                ctx.fillRect(p.x - s / 2, p.y - s / 2, s, s);
            }
            ctx.globalCompositeOperation = 'source-over';
        };

        const loop = (time: number) => {
            if (destroyed || !running) return;
            drawFrame(time);
            rafId = requestAnimationFrame(loop);
        };

        const start = () => {
            if (running || destroyed) return;
            running = true;
            rafId = requestAnimationFrame(loop);
        };
        const stop = () => {
            running = false;
            cancelAnimationFrame(rafId);
        };

        const onPointerMove = (e: PointerEvent) => {
            const rect = canvas.getBoundingClientRect();
            mouse.x = e.clientX - rect.left;
            mouse.y = e.clientY - rect.top;
        };
        const onPointerLeave = () => {
            mouse.x = -9999;
            mouse.y = -9999;
        };

        let resizeTimer = 0;
        const onResize = () => {
            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(resize, 150);
        };

        resize();

        if (reduced) {
            // No continuous animation: render on scroll only, snapping to targets.
            const onScroll = () => drawFrame(performance.now());
            window.addEventListener('scroll', onScroll, {passive: true});
            drawFrame(performance.now());
            window.addEventListener('resize', onResize);
            return () => {
                destroyed = true;
                window.removeEventListener('scroll', onScroll);
                window.removeEventListener('resize', onResize);
                window.clearTimeout(resizeTimer);
            };
        }

        const io = new IntersectionObserver(
            entries => entries.forEach(en => (en.isIntersecting ? start() : stop())),
            {threshold: 0}
        );
        io.observe(section);

        window.addEventListener('resize', onResize);
        window.addEventListener('pointermove', onPointerMove, {passive: true});
        window.addEventListener('pointerleave', onPointerLeave);

        return () => {
            destroyed = true;
            stop();
            io.disconnect();
            window.removeEventListener('resize', onResize);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerleave', onPointerLeave);
            window.clearTimeout(resizeTimer);
        };
    }, []);

    // Heavy-spring scroll physics for the snap panels: inside the divisions
    // region, wheel input is hijacked and drives a damped spring. Scrolling
    // feels like pushing something heavy that snaps into the three division
    // positions. Native scrolling resumes outside the region (and pushing
    // firmly past the edge breaks free of it). Touch devices fall back to a
    // gentle settle-snap after scrolling stops.
    useEffect(() => {
        const section = sectionRef.current;
        if (!section) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const snapIndexes = panels
            .map((p, i) => (p.snap ? i : -1))
            .filter(i => i >= 0);
        if (!snapIndexes.length) return;

        const firstK = snapIndexes[0];
        const lastK = snapIndexes[snapIndexes.length - 1];
        const doc = document.documentElement;

        // Spring tuning: slightly underdamped so it settles with a soft,
        // feathered overshoot; low impulse gain makes the push feel heavy.
        const K = 22; // stiffness (1/s²)
        const C = 9; // damping (1/s); near-critical (2*sqrt(K) ≈ 9.4) for minimal overshoot
        const IMPULSE = 15; // wheel delta → velocity gain
        const CATCH = 0.45; // velocity kept when crossing into the next item's pull
        const MAX_VEL = 6000; // px/s
        const EDGE = 0.28; // how far past the edge you must push to break free

        let engaged = false;
        let pos = 0;
        let vel = 0;
        let rafId = 0;
        let lastT = 0;
        let prevIdx = -1;

        const anchors = () => {
            const vh = window.innerHeight || 1;
            const top = section.getBoundingClientRect().top + window.scrollY;
            return {vh, first: top + firstK * vh, last: top + lastK * vh};
        };

        const release = () => {
            engaged = false;
            cancelAnimationFrame(rafId);
            doc.style.scrollBehavior = '';
        };

        const step = (t: number) => {
            if (!engaged) return;
            const dt = Math.min(0.033, Math.max(0.001, (t - lastT) / 1000));
            lastT = t;
            const {vh, first, last} = anchors();

            // Break free when pushed firmly past the region edges
            if ((pos < first - vh * EDGE && vel < 0) || (pos > last + vh * EDGE && vel > 0)) {
                release();
                return;
            }

            const idx = Math.round((pos - first) / vh);
            const clamped = Math.min(snapIndexes.length - 1, Math.max(0, idx));
            const target = first + clamped * vh;

            // Crossing into the next item's pull: bleed off most of the
            // momentum so it can't barrel on toward the item after that.
            if (prevIdx !== -1 && clamped !== prevIdx) {
                vel *= CATCH;
            }
            prevIdx = clamped;

            const acc = (target - pos) * K - vel * C;
            vel += acc * dt;
            pos += vel * dt;
            window.scrollTo(0, pos);

            // Settled?
            if (Math.abs(target - pos) < 0.6 && Math.abs(vel) < 12) {
                window.scrollTo(0, target);
                release();
                return;
            }
            rafId = requestAnimationFrame(step);
        };

        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey) return; // don't break pinch-zoom
            const {vh, first, last} = anchors();
            const y = window.scrollY;
            const inRegion = y > first - vh * 0.22 && y < last + vh * 0.22;
            if (!engaged && !inRegion) return;

            e.preventDefault();

            let d = e.deltaY;
            if (e.deltaMode === 1) d *= 16;
            else if (e.deltaMode === 2) d *= vh;

            if (!engaged) {
                engaged = true;
                pos = y;
                vel = 0;
                prevIdx = -1;
                lastT = performance.now();
                doc.style.scrollBehavior = 'auto'; // instant programmatic scrolls
                rafId = requestAnimationFrame(step);
            }
            vel = Math.max(-MAX_VEL, Math.min(MAX_VEL, vel + d * IMPULSE));
        };

        // Fallback for touch / keyboard: settle-snap after scrolling stops
        let timer = 0;
        let settleGuard = false;
        let lastY = window.scrollY;

        const trySnap = () => {
            if (engaged) return;
            const {vh, first, last} = anchors();
            const y = window.scrollY;
            if (y < first - vh * 0.2 || y > last + vh * 0.2) return;
            for (let i = 0; i < snapIndexes.length; i++) {
                const target = first + i * vh;
                const diff = target - y;
                if (Math.abs(diff) < vh * 0.5 && Math.abs(diff) > 2) {
                    settleGuard = true;
                    window.scrollTo({top: target, behavior: 'smooth'});
                    window.setTimeout(() => {
                        settleGuard = false;
                    }, 700);
                    return;
                }
            }
        };

        const onScroll = () => {
            if (engaged || settleGuard) return;
            if (Math.abs(window.scrollY - lastY) < 1) return;
            lastY = window.scrollY;
            window.clearTimeout(timer);
            timer = window.setTimeout(trySnap, 160);
        };

        window.addEventListener('wheel', onWheel, {passive: false});
        window.addEventListener('scroll', onScroll, {passive: true});
        return () => {
            release();
            window.removeEventListener('wheel', onWheel);
            window.removeEventListener('scroll', onScroll);
            window.clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <section className="morph-story" ref={sectionRef}>
            <div className="morph-story__sticky" aria-hidden="true">
                <canvas ref={canvasRef}/>
            </div>
            <div className="morph-story__panels">
                {panels.map((panel, idx) => (
                    <div className={`morph-panel${panel.snap ? ' morph-panel--snap' : ''}`} key={idx}>
                        {panel.content}
                    </div>
                ))}
            </div>
        </section>
    );
};

export default ParticleMorphStory;
